import PageShell from "@/components/PageShell";
import PageHero from "@/components/PageHero";
import PatentFiled from "@/components/PatentFiled";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";

const ROADMAP = [
    { phase: "Phase 1", t: "Website · Whitepaper · Patent · Prototype", status: "done" },
    { phase: "Phase 2", t: "Devnet", status: "active" },
    { phase: "Phase 3", t: "Testnet", status: "next" },
    { phase: "Phase 4", t: "Node Onboarding", status: "next" },
    { phase: "Phase 5", t: "Mainnet Beta", status: "next" },
    { phase: "Phase 6", t: "Ecosystem Expansion", status: "next" },
];

const ECOSYSTEM = ["Developers", "Node Operators", "Validators", "GPU Providers", "Storage Operators", "Partners", "Investors"];

export default function AboutPage() {
    return (
        <PageShell>
            <PageHero
                eyebrow="About"
                title="Building the OS of"
                accent="the Decentralized Internet."
                sub="DeCloudX Foundation is rebuilding cloud infrastructure as an open, programmable, user-owned network — coordinated by a patent-filed Layer-1 EVM chain."
            />

            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Mission</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">
                        To rebuild cloud infrastructure as an open, programmable, user-owned network.
                    </h2>
                    <p className="mt-6 text-base text-muted-ink leading-relaxed max-w-2xl">
                        The internet's most valuable layer — its cloud — is owned by a handful of companies. DeCloudX makes the cloud programmable, verifiable, and shared among the people who build, run, and depend on it.
                    </p>
                </div>
            </section>

            <PatentFiled />

            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Roadmap</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">Path to mainnet and ecosystem.</h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="roadmap">
                        {ROADMAP.map((r, i) => (
                            <div key={i} className={`surface card-lift p-6 rounded-2xl ${r.status === "active" ? "ring-1 ring-brand-blue/40" : ""}`} data-testid={`roadmap-${i}`}>
                                <div className="flex items-center justify-between">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">{r.phase}</div>
                                    {r.status === "done" && <CheckCircle2 size={15} className="text-emerald-500" strokeWidth={1.5} />}
                                    {r.status === "active" && <span className="font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue border border-brand-blue/30">Active</span>}
                                    {r.status === "next" && <span className="font-mono text-[9px] uppercase tracking-wider text-faint-ink">Upcoming</span>}
                                </div>
                                <div className="font-heading font-bold text-base text-ink mt-2">{r.t}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Ecosystem</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">A protocol that aligns every stakeholder.</h2>
                    <div className="mt-8 flex flex-wrap gap-2">
                        {ECOSYSTEM.map((e, i) => <span key={i} className="chip" data-testid={`eco-${i}`}>{e}</span>)}
                    </div>
                    <div className="mt-12">
                        <Link to="/contact?role=partner" className="btn-primary">Partner With DeCloudX <ArrowRight size={15} strokeWidth={2} /></Link>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
